import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router';
import { Tooltip, Box, Typography } from '@mui/material';
import {
  DashboardOutlined as DashboardIcon,
  DescriptionOutlined as DescriptionIcon,
  AccountTreeOutlined as AccountTreeIcon,
  SettingsOutlined as SettingsIcon,
  SettingsInputComponentOutlined as SettingsInputIcon,
  PeopleOutlined as PeopleIcon,
  AssessmentOutlined as AssessmentIcon,
  CameraAltOutlined as CameraAltIcon,
  FormatListBulletedOutlined as FormatListBulletedIcon,
  GridViewOutlined as GridViewIcon,
  WorkOutlined as WorkIcon,
  LogoutOutlined as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  MenuBookOutlined as MenuBookIcon,
  LightModeOutlined as LightModeIcon,
  DarkModeOutlined as DarkModeIcon,
} from '@mui/icons-material';

import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { setDataType, setJobSchedule } from '../../store/slices/authSlice';
import {
  CONSTANTS,
  CronExpression,
  FALCONDATATYPE,
  APP_HEADER,
} from '../../common/constants/constants';
import { useLogoutMutation } from '../../services/authApi';
import { useThemeMode } from '../../theme/ThemeContext';

import sidebarFaviconFallback from '../../assets/smallLogo.svg';
import WDayLogo from '../common/WDayLogo';

const COLLAPSED_LOGO = '/Favicon.png';
const SNAPSHOT_PATH = '/employee-master/snapshot';

interface NavItem {
  key: string;
  label: string;
  icon: React.ElementType;
  path?: string;
  privilege?: string;
  parentPrivilege?: string;
  children?: {
    label: string;
    path: string;
    icon: React.ElementType;
    privilege?: string;
    onClick?: () => void;
  }[];
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  width: number;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const dispatch = useAppDispatch();
  const privileges = useAppSelector((state) => state.auth?.user?.privileges);
  const location = useLocation();
  const loggedInUser = useAppSelector((state) => state.auth?.user);
  const [logoutApi] = useLogoutMutation();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const { mode: themeMode, toggleTheme } = useThemeMode();

  const [collapsedLogoSrc, setCollapsedLogoSrc] = useState(COLLAPSED_LOGO);

  useEffect(() => {
    setCollapsedLogoSrc(COLLAPSED_LOGO);
  }, [collapsed]);

  const handleCollapsedLogoError = () => {
    setCollapsedLogoSrc((current) =>
      current === sidebarFaviconFallback ? current : sidebarFaviconFallback
    );
  };

  const displayName =
    `${loggedInUser?.firstName ?? ''} ${loggedInUser?.lastName ?? ''}`.trim() ||
    APP_HEADER.DEFAULT_USER_NAME;
  const position = loggedInUser?.position ?? '';
  const initialSource = loggedInUser?.firstName ?? APP_HEADER.DEFAULT_USER_INITIAL;
  const lastNameSource = loggedInUser?.lastName ?? '';
  const initial =
    (
      (initialSource.charAt(0) || APP_HEADER.DEFAULT_USER_INITIAL) +
      (lastNameSource.charAt(0) || '')
    ).toUpperCase() || APP_HEADER.DEFAULT_USER_INITIAL;

  const checkPrivilege = useCallback(
    (priv: string): boolean => {
      const match = privileges?.filter((p) => p.type === priv);
      return match?.[0]?.privilege ?? false;
    },
    [privileges]
  );

  const checkMainMenuPrivilege = useCallback(
    (menu: string): boolean => {
      return menu.split(/\s*,\s*/).some((childMenu) => {
        const match = privileges?.filter((p) => p.type === childMenu);
        return match?.[0]?.privilege ?? false;
      });
    },
    [privileges]
  );

  const isSnapshotRoute = location.pathname === '/' || location.pathname === SNAPSHOT_PATH;

  const isActive = (path: string) => {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    if (normalized === SNAPSHOT_PATH && isSnapshotRoute) return true;
    return location.pathname === normalized || location.pathname.startsWith(`${normalized}/`);
  };

  const isSectionActive = useCallback(
    (section: string) => {
      if (section === 'employee-master' && isSnapshotRoute) return true;
      const paths: Record<string, string> = {
        'employee-master': '/employee-master',
        'auto-provisioning': '/auto-provisioning',
        falcon: '/falcon',
        projects: '/projects',
        maestro: '/maestro',
      };
      return location.pathname.startsWith(paths[section] ?? `/${section}`);
    },
    [location.pathname, isSnapshotRoute]
  );

  useEffect(() => {
    if (collapsed) return;
    setOpenSections({
      'employee-master': isSectionActive('employee-master'),
      'auto-provisioning': isSectionActive('auto-provisioning'),
      falcon: isSectionActive('falcon'),
      projects: isSectionActive('projects'),
      maestro: isSectionActive('maestro'),
    });
  }, [location.pathname, collapsed, isSectionActive]);

  const toggleSection = (key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const logout = async () => {
    try {
      await logoutApi().unwrap();
      localStorage.clear();
      window.setTimeout(() => {
        window.location.href = APP_HEADER.LOGIN_REDIRECT_PATH;
      }, 300);
    } catch {
      console.error(APP_HEADER.LOGOUT_ERROR);
    }
  };

  const navItems: NavItem[] = [
    {
      key: 'employee-master',
      label: 'Employee Master',
      icon: DescriptionIcon,
      parentPrivilege: `${CONSTANTS.EMPLOYEE_MASTER_SNAPSHOT},${CONSTANTS.EMPLOYEE_MASTER_DASHBOARD},${CONSTANTS.EMPLOYEE_MASTER_SNAPSHOT_LOGS}`,
      children: [
        {
          label: 'Snapshot',
          path: SNAPSHOT_PATH,
          icon: CameraAltIcon,
          privilege: CONSTANTS.EMPLOYEE_MASTER_SNAPSHOT,
        },
        {
          label: 'Dashboard',
          path: '/employee-master/dashboard',
          icon: DashboardIcon,
          privilege: CONSTANTS.EMPLOYEE_MASTER_DASHBOARD,
        },
        {
          label: 'Logs',
          path: '/employee-master/logs',
          icon: FormatListBulletedIcon,
          privilege: CONSTANTS.EMPLOYEE_MASTER_SNAPSHOT_LOGS,
        },
      ],
    },
    {
      key: 'auto-provisioning',
      label: 'Auto Provisioning',
      icon: AccountTreeIcon,
      parentPrivilege: 'AP_PROHANCE,AP_PIXIE',
      children: [
        {
          label: 'Prohance',
          path: '/auto-provisioning/prohance',
          icon: WorkIcon,
          privilege: 'AP_PROHANCE',
          onClick: () => {
            if (location.pathname !== '/auto-provisioning/prohance')
              dispatch(setJobSchedule(CronExpression[0]));
          },
        },
        {
          label: 'Manage Jobs',
          path: '/auto-provisioning/manage-jobs',
          icon: SettingsIcon,
          privilege: 'AP_GLOBAL_JOB',
        },
        {
          label: 'Configure',
          path: '/auto-provisioning/configure',
          icon: SettingsInputIcon,
          privilege: 'AP_PIXIE',
          onClick: () => {
            if (location.pathname !== '/auto-provisioning/configure') {
              dispatch(setDataType(FALCONDATATYPE[0]));
              dispatch(setJobSchedule(CronExpression[0]));
            }
          },
        },
      ],
    },
    {
      key: 'falcon',
      label: 'Falcon',
      icon: AssessmentIcon,
      parentPrivilege: 'FALCON_LOGS',
      children: [
        {
          label: 'Logs',
          path: '/falcon/logs',
          icon: FormatListBulletedIcon,
          privilege: 'FALCON_LOGS',
        },
      ],
    },
    {
      key: 'projects',
      label: 'Automation Project',
      icon: MenuBookIcon,
      parentPrivilege: 'AUTOMATION_PROJECT',
      children: [
        {
          label: 'Overview',
          path: '/projects/overview',
          icon: GridViewIcon,
        },
      ],
    },
    {
      key: 'maestro',
      label: 'Maestro',
      icon: PeopleIcon,
      parentPrivilege: 'Maestro Auto Provisioning',
      children: [
        {
          label: 'Auto Provisioning',
          path: '/maestro/auto-provisioning',
          icon: AccountTreeIcon,
          privilege: 'AP_PROHANCE',
        },
      ],
    },
  ];

  const filteredNavItems = navItems.filter((item) => {
    if (item.parentPrivilege && !checkMainMenuPrivilege(item.parentPrivilege)) return false;
    return true;
  });

  const renderCollapsedItem = (item: NavItem) => {
    const Icon = item.icon;
    const active = isSectionActive(item.key);
    const firstChildPath = item.children?.[0]?.path ?? '#';

    const visibleChildren =
      item.children?.filter((child) => {
        if (child.privilege && !checkPrivilege(child.privilege)) return false;
        return true;
      }) ?? [];

    const isLight = themeMode === 'light';
    const popoverBg = isLight ? 'var(--bg-primary, #ffffff)' : 'rgba(15, 23, 42, 0.95)';
    const popoverBorder = isLight
      ? '1px solid var(--border-light, #e6e6e6)'
      : '1px solid rgba(255, 255, 255, 0.1)';
    const popoverShadow = isLight
      ? '0 4px 18px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)'
      : '0 4px 20px rgba(0, 0, 0, 0.25)';

    const headerColor = isLight ? 'var(--text-secondary, #64748b)' : 'rgba(255, 255, 255, 0.6)';
    const headerBorder = isLight
      ? '1px solid var(--border-light, #f1f5f9)'
      : '1px solid rgba(255, 255, 255, 0.15)';

    const itemTextColor = (childActive: boolean) => {
      if (childActive) {
        return isLight ? 'var(--primary, #1f3dff)' : '#ffffff';
      }
      return isLight ? 'var(--text-secondary, #475569)' : 'rgba(255, 255, 255, 0.8)';
    };

    const itemBg = (childActive: boolean) => {
      if (childActive) {
        return isLight ? 'rgba(31, 61, 255, 0.08)' : 'rgba(255, 255, 255, 0.15)';
      }
      return 'transparent';
    };

    const itemHoverBg = isLight
      ? 'var(--hover-bg, rgba(102, 122, 255, 0.06))'
      : 'rgba(255, 255, 255, 0.1)';
    const itemHoverColor = isLight ? 'var(--primary, #1f3dff)' : '#ffffff';

    const tooltipTitle =
      visibleChildren.length > 0 ? (
        <Box sx={{ p: 0.5, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              px: 1,
              py: 0.5,
              fontWeight: 600,
              color: headerColor,
              borderBottom: headerBorder,
              textTransform: 'uppercase',
              fontSize: '10px',
              letterSpacing: '0.05em',
              display: 'block',
              mb: 0.5,
            }}
          >
            {item.label}
          </Typography>
          {visibleChildren.map((child) => {
            const ChildIcon = child.icon;
            const childActive = isActive(child.path);
            return (
              <Link
                key={child.path}
                to={child.path}
                onClick={child.onClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  textDecoration: 'none',
                  color: itemTextColor(childActive),
                  background: itemBg(childActive),
                  fontWeight: childActive ? 600 : 400,
                  fontSize: '11px',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = itemHoverBg;
                  e.currentTarget.style.color = itemHoverColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = itemBg(childActive);
                  e.currentTarget.style.color = itemTextColor(childActive);
                }}
              >
                <ChildIcon style={{ fontSize: 13, color: 'inherit' }} />
                <span>{child.label}</span>
              </Link>
            );
          })}
        </Box>
      ) : (
        item.label
      );

    return (
      <Tooltip
        key={item.key}
        title={tooltipTitle}
        placement="right"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: `${popoverBg} !important`,
              backdropFilter: isLight ? 'none' : 'blur(4px)',
              boxShadow: popoverShadow,
              border: popoverBorder,
              borderRadius: '8px',
              p: 0.5,
              color: isLight ? 'var(--text-primary) !important' : 'inherit',
            },
          },
        }}
      >
        <Link to={firstChildPath} className="block">
          <div
            className={`mx-auto flex h-9 w-9 items-center justify-center rounded-lg transition-all duration-200
              ${active ? 'bg-primary/10 text-primary dark:bg-primary-light/20 dark:text-white font-semibold' : 'text-[var(--text-secondary)] hover:bg-[var(--hover-bg)] hover:text-[var(--primary)]'}`}
          >
            <Icon style={{ fontSize: 18 }} />
          </div>
        </Link>
      </Tooltip>
    );
  };

  const renderExpandedItem = (item: NavItem) => {
    const Icon = item.icon;
    const isOpen = openSections[item.key] ?? false;
    const active = isSectionActive(item.key);

    const visibleChildren = item.children?.filter((child) => {
      if (child.privilege && !checkPrivilege(child.privilege)) return false;
      return true;
    });

    return (
      <div key={item.key}>
        <button
          onClick={() => toggleSection(item.key)}
          className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-all duration-200
            ${active ? 'bg-gradient-to-r from-primary to-primary-light text-white shadow-md shadow-primary/25' : 'text-[var(--text-secondary)] hover:bg-[var(--hover-bg)] hover:text-[var(--primary)]'}`}
        >
          <Icon style={{ fontSize: 18 }} />
          <span className="flex-1 truncate text-[0.8rem] font-medium">{item.label}</span>
          <KeyboardArrowDownIcon
            style={{ fontSize: 16 }}
            className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${active ? 'text-white/70' : 'text-gray-400'}`}
          />
        </button>

        <div
          className={`overflow-hidden transition-all duration-200 ${isOpen ? 'mt-1 max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
        >
          <div className="ml-2 space-y-0.5 border-l-2 border-primary/20 dark:border-primary-light/30 pl-3">
            {visibleChildren?.map((child) => {
              const ChildIcon = child.icon;
              const childActive = isActive(child.path);
              return (
                <Link
                  key={child.path}
                  to={child.path}
                  onClick={child.onClick}
                  className={`flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[0.75rem] font-medium transition-all duration-200
                    ${childActive ? 'bg-primary/10 text-primary dark:bg-primary-light/20 dark:text-white font-semibold' : 'text-[var(--text-secondary)] hover:bg-[var(--hover-bg)] hover:text-[var(--text-primary)]'}`}
                >
                  <ChildIcon style={{ fontSize: 15 }} />
                  <span>{child.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <aside
      className={`relative flex h-screen flex-col border-r transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
        ${collapsed ? 'w-[60px]' : 'w-[240px]'}`}
      style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-light)' }}
    >
      {/* Logo */}
      <div
        className={`flex shrink-0 items-center border-b ${collapsed ? 'justify-center px-2 py-3' : 'justify-between px-4 py-3'}`}
        style={{ borderColor: 'var(--border-light)' }}
      >
        <Link to={SNAPSHOT_PATH} className="flex items-center gap-2.5">
          {collapsed ? (
            <img
              src={collapsedLogoSrc}
              onError={handleCollapsedLogoError}
              alt="WDay"
              className="h-7 w-7 rounded object-contain transition-all duration-300"
            />
          ) : (
            <WDayLogo />
          )}
        </Link>
        {!collapsed && (
          <button
            onClick={onToggle}
            aria-label="Collapse sidebar"
            className="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-[var(--gray-01)] hover:text-gray-600"
          >
            <ChevronLeftIcon style={{ fontSize: 16 }} />
          </button>
        )}
      </div>

      {/* Collapsed expand button */}
      {collapsed && (
        <div className="flex justify-center py-2">
          <button
            onClick={onToggle}
            aria-label="Expand sidebar"
            className="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-[var(--gray-01)] hover:text-gray-600"
          >
            <ChevronRightIcon style={{ fontSize: 16 }} />
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-2">
        {!collapsed && (
          <p className="mb-2 px-2 text-[0.6rem] font-bold uppercase tracking-widest text-[var(--text-tertiary)]">
            Navigation
          </p>
        )}
        <div className={`space-y-0.5 ${collapsed ? 'space-y-1' : ''}`}>
          {filteredNavItems.map((item) =>
            collapsed ? renderCollapsedItem(item) : renderExpandedItem(item)
          )}
        </div>
      </nav>

      {/* User Footer */}
      <div className="shrink-0 border-t px-2 py-3" style={{ borderColor: 'var(--border-light)' }}>
        {collapsed ? (
          <div className="flex flex-col items-center gap-2">
            <Tooltip title={displayName} placement="right">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-[0.6rem] font-bold text-white">
                {initial}
              </div>
            </Tooltip>

            {/* Theme Toggle Button */}
            <Tooltip
              title={themeMode === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
              placement="right"
            >
              <button
                onClick={toggleTheme}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--text-secondary)] transition-colors hover:bg-[var(--hover-bg)] hover:text-[var(--primary)]"
              >
                {themeMode === 'light' ? (
                  <DarkModeIcon style={{ fontSize: 16 }} />
                ) : (
                  <LightModeIcon style={{ fontSize: 16 }} />
                )}
              </button>
            </Tooltip>

            <Tooltip title="Logout" placement="right">
              <button
                onClick={logout}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--text-secondary)] transition-colors hover:bg-red-500/10 hover:text-red-500"
              >
                <LogoutIcon style={{ fontSize: 16 }} />
              </button>
            </Tooltip>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-[0.65rem] font-bold text-white">
                {initial}
              </div>
              <div className="min-w-0 flex-1">
                <p
                  className="truncate text-xs font-semibold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {displayName}
                </p>
                <p className="truncate text-[0.65rem]" style={{ color: 'var(--text-secondary)' }}>
                  {position}
                </p>
              </div>
            </div>

            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-1.5 text-[var(--text-secondary)] transition-all duration-200 hover:bg-[var(--hover-bg)] hover:text-[var(--primary)]"
            >
              {themeMode === 'light' ? (
                <>
                  <DarkModeIcon style={{ fontSize: 15 }} />
                  <span className="text-xs font-medium">Dark Mode</span>
                </>
              ) : (
                <>
                  <LightModeIcon style={{ fontSize: 15 }} />
                  <span className="text-xs font-medium">Light Mode</span>
                </>
              )}
            </button>

            <button
              onClick={logout}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-1.5 text-[var(--text-secondary)] transition-all duration-200 hover:bg-red-500/10 hover:text-red-500"
            >
              <LogoutIcon style={{ fontSize: 15 }} />
              <span className="text-xs font-medium">Logout</span>
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
